import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePlaybooks } from '../hooks/usePlaybooks';
import { migrateLocalToCloud } from '../utils/migration';
import PlaybookWizard from './PlaybookWizard';

const HomeView = ({ onSelectPlaybook }) => {
    const { currentUser, isMasterMode, logout } = useAuth();
    const { playbooks, loading: pbLoading, createPlaybook, duplicatePlaybook, updatePlaybook, deletePlaybook } = usePlaybooks();
    const [isCreating, setIsCreating] = useState(false);
    const [contextMenu, setContextMenu] = useState(null);

    // Migration state
    const [isMigrating, setIsMigrating] = useState(false);
    const [migrationProgress, setMigrationProgress] = useState({ current: 0, total: 0 });

    const localDataStr = localStorage.getItem('football_playbook_data');
    const localPlayCount = React.useMemo(() => {
        if (!localDataStr) return 0;
        try {
            const data = JSON.parse(localDataStr);
            let count = 0;
            // Iterate types (Offense/Defense)
            Object.values(data).forEach(teamTypeData => {
                // Iterate playbooks
                Object.values(teamTypeData.playbooks || {}).forEach(pb => {
                    count += Object.keys(pb.plays || {}).length;
                });
            });
            return count;
        } catch (e) { return 0; }
    }, [localDataStr]);

    const handleCreate = async (data) => {
        try {
            const payload = {
                profile: { level: data.level, format: data.format },
                labelMode: data.labelMode,
                customLabels: {}
            };
            await createPlaybook(data.name, payload);
            setIsCreating(false);
        } catch (err) {
            console.error("Failed to create playbook:", err);
            throw err;
        }
    };

    const handleDuplicate = async (pbId) => {
        const pb = playbooks.find(p => p.id === pbId);
        if (!pb) return;

        const isOwner = pb.ownerId === currentUser?.uid;
        if (!isOwner && pb.preventDuplication) {
            alert("Duplication is disabled for this playbook by the owner.");
            return;
        }

        if (window.confirm(`Duplicate "${pb.name}"? This will copy all plays.`)) {
            try {
                await duplicatePlaybook(pbId);
            } catch (err) {
                alert("Failed to duplicate: " + err.message);
            }
        }
    };

    const handleToggleDuplication = async (pbId, currentVal) => {
        try {
            await updatePlaybook(pbId, { preventDuplication: !currentVal });
        } catch (err) {
            alert("Failed to update settings: " + err.message);
        }
    };

    const handleRename = async (pbId, currentName) => {
        const newName = window.prompt("Enter new playbook name:", currentName);
        if (newName && newName.trim() !== "" && newName !== currentName) {
            try {
                await updatePlaybook(pbId, { name: newName.trim() });
            } catch (err) {
                alert("Failed to rename: " + err.message);
            }
        }
    };

    const handleDelete = async (pbId, pb) => {
        const otherCollaborators = Object.values(pb.collaborators || {})
            .filter(c => c.email !== currentUser.email)
            .map(c => c.displayName || c.email);

        const shareWarning = otherCollaborators.length > 0
            ? `\n\nWarning: This playbook is shared with: ${otherCollaborators.join(', ')}. They will lose access if you delete it.`
            : "";

        const input = window.prompt(
            `Are you sure you want to delete "${pb.name}"? This cannot be undone.${shareWarning}\n\nType DELETE to confirm:`
        );

        if (input === 'DELETE') {
            try {
                await deletePlaybook(pbId);
            } catch (err) {
                alert("Failed to delete: " + err.message);
            }
        } else if (input !== null) {
            alert("Deletion cancelled. You must type DELETE exactly.");
        }
    };

    const handleMigrate = async () => {
        if (!window.confirm("This will copy your local plays to the cloud. Proceed?")) return;
        setIsMigrating(true);
        try {
            await migrateLocalToCloud(currentUser, (current, total) => {
                setMigrationProgress({ current, total });
            });
            alert("Migration complete! Your plays are now in the cloud.");
        } catch (err) {
            alert("Migration failed: " + err.message);
        } finally {
            setIsMigrating(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-950 text-gray-100 flex flex-col" onClick={() => setContextMenu(null)}>
            {/* Top Navbar */}
            <header className="bg-gray-900 border-b border-gray-800 px-6 py-4 flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <img
                        src="/logo.png"
                        alt="QuickRoute"
                        className="h-10 w-auto"
                        style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                    />
                </div>

                <div className="flex items-center gap-4">
                    <div className="text-right hidden sm:block">
                        <p className="text-sm font-medium">{currentUser?.displayName || 'Local User'}</p>
                        <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">
                            {isMasterMode ? 'Master Mode' : 'Cloud Synchronized'}
                        </p>
                    </div>
                    <button
                        onClick={logout}
                        className="px-4 py-2 text-sm bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors"
                    >
                        Log Out
                    </button>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 max-w-7xl w-full mx-auto p-8">
                {isMigrating && (
                    <div className="mb-8 p-6 bg-blue-600/10 border border-blue-500/20 rounded-2xl flex items-center justify-between animate-pulse">
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            <div>
                                <h3 className="font-bold text-blue-400">Migrating Legacy Portfolio...</h3>
                                <p className="text-sm text-gray-400">Moving your local plays to Firestore subcollections.</p>
                            </div>
                        </div>
                        <div className="text-2xl font-black text-blue-500">
                            {Math.round((migrationProgress.current / migrationProgress.total) * 100)}%
                        </div>
                    </div>
                )}

                {/* Wizard Modal */}
                {isCreating && (
                    <PlaybookWizard
                        onClose={() => setIsCreating(false)}
                        onCreate={handleCreate}
                    />
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

                    {/* Create New Playbook */}
                    {!isMasterMode && (
                        <button
                            onClick={() => setIsCreating(true)}
                            className="h-64 border-2 border-dashed border-gray-800 hover:border-blue-500/50 hover:bg-blue-500/5 rounded-2xl flex flex-col items-center justify-center p-6 transition-all group"
                        >
                            <div className="w-12 h-12 rounded-full bg-gray-900 border border-gray-800 flex items-center justify-center group-hover:scale-110 group-hover:bg-blue-600 group-hover:border-blue-500 transition-all">
                                <span className="text-2xl">+</span>
                            </div>
                            <span className="font-bold text-gray-400 group-hover:text-white mt-4">Create New Playbook</span>
                        </button>
                    )}

                    {/* Cloud Playbooks */}
                    {!isMasterMode && playbooks.map(pb => {
                        const isOwner = pb.ownerId === currentUser?.uid;
                        const isShared = pb.collaboratorIds?.length > 1;

                        return (
                            <div
                                key={pb.id}
                                className="h-64 bg-gray-900 border border-gray-800 hover:border-blue-500 hover:shadow-2xl hover:shadow-blue-500/10 rounded-2xl p-6 flex flex-col items-start transition-all text-left relative overflow-hidden group cursor-pointer"
                                onClick={() => onSelectPlaybook(pb.id)}
                                onContextMenu={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setContextMenu({
                                        x: e.clientX,
                                        y: e.clientY,
                                        pb
                                    });
                                }}
                            >
                                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity pointer-events-none">
                                    <img src="/logo.png" alt="" className="w-32 h-auto grayscale brightness-200" style={{ transform: 'rotate(-15deg) translate(20%, -20%)' }} />
                                </div>
                                <div className="flex gap-2 mb-4">
                                    <div className="bg-green-600/20 text-green-400 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded">
                                        Cloud Sync
                                    </div>
                                    {isShared && (
                                        <div className="bg-orange-600 text-white text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded shadow-sm shadow-orange-500/20">
                                            Shared
                                        </div>
                                    )}
                                </div>
                                <h3 className="text-xl font-bold mb-1 truncate w-full">{pb.name}</h3>
                                <div className="flex gap-2 mb-4">
                                    <span className="text-[10px] bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded border border-gray-700 font-bold uppercase tracking-wider">
                                        {pb.profile?.level || 'HS'} · {pb.profile?.format || '6v6'}
                                    </span>
                                    <span className="text-[10px] bg-blue-600/20 text-blue-400 px-1.5 py-0.5 rounded border border-blue-500/20 font-bold">
                                        {pb.playCount || 0} Plays
                                    </span>
                                </div>
                                {pb.notes && (
                                    <p className="text-[11px] text-gray-400 mb-4 line-clamp-2 italic">
                                        "{pb.notes}"
                                    </p>
                                )}
                                <p className="text-[10px] text-gray-500 mb-6 mt-auto">
                                    Last updated {pb.updatedAt?.toDate ? pb.updatedAt.toDate().toLocaleDateString() : 'Just now'}
                                </p>
                                <div className="w-full h-10 bg-gray-800 group-hover:bg-blue-600 flex items-center justify-center rounded-lg font-bold text-sm transition-colors mt-auto">
                                    Open Playbook
                                </div>
                            </div>
                        );
                    })}

                    {/* Local Portfolio */}
                    <div
                        className={`h-64 border rounded-2xl p-6 flex flex-col items-start transition-all text-left relative overflow-hidden group ${isMasterMode ? 'bg-blue-900/10 border-blue-500/50' : 'bg-gray-900 border-gray-800 hover:border-white/20'}`}
                    >
                        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                            <span className="text-8xl">📁</span>
                        </div>
                        <div className={`${isMasterMode ? 'bg-blue-600 text-white' : 'bg-blue-600/20 text-blue-400'} text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded mb-4`}>
                            {isMasterMode ? 'Direct Access' : 'Local Browser Data'}
                        </div>
                        <h3 className="text-xl font-bold mb-1">Portfolio (Legacy)</h3>
                        <div className="mb-4">
                            <span className="text-[10px] bg-blue-600/20 text-blue-400 px-1.5 py-0.5 rounded border border-blue-500/20 font-bold">
                                {localPlayCount} Plays Total
                            </span>
                        </div>
                        <p className="text-sm text-gray-500 mb-6 flex-1">
                            {isMasterMode
                                ? "You are viewing your local development plays directly."
                                : "Access plays saved in your local storage from the dev build."}
                        </p>

                        <div className="flex gap-2 w-full mt-auto">
                            <button
                                onClick={() => onSelectPlaybook('local')}
                                className="flex-1 h-10 bg-gray-800 hover:bg-blue-600 flex items-center justify-center rounded-lg font-bold text-sm transition-colors"
                            >
                                Open Editor
                            </button>
                            {!isMasterMode && localStorage.getItem('football_playbook_data') && (
                                <button
                                    onClick={handleMigrate}
                                    title="Import all local plays to Cloud"
                                    className="px-3 h-10 bg-green-600/20 hover:bg-green-600 text-green-400 hover:text-white rounded-lg transition-all"
                                >
                                    ☁️ ↑
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Loading State */}
                    {!isMasterMode && pbLoading && playbooks.length === 0 && (
                        <div className="h-64 bg-gray-900/40 border border-gray-800/50 rounded-2xl p-6 flex flex-col items-center justify-center animate-pulse">
                            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                            <p className="text-center text-gray-500 text-sm italic">
                                Loading your cloud playbooks...
                            </p>
                        </div>
                    )}
                </div>
            </main>

            {/* Playbook Context Menu */}
            {contextMenu && (
                <div
                    className="fixed bg-gray-900 border border-gray-700 rounded-lg shadow-2xl py-2 z-[100] min-w-[200px]"
                    style={{ left: contextMenu.x, top: contextMenu.y }}
                    onClick={e => e.stopPropagation()}
                >
                    <div className="px-4 py-2 border-b border-gray-800 text-[10px] text-gray-500 font-bold uppercase tracking-widest">
                        {contextMenu.pb.name} Options
                    </div>
                    <button
                        onClick={() => { handleDuplicate(contextMenu.pb.id); setContextMenu(null); }}
                        className="w-full text-left px-4 py-2 hover:bg-blue-600 hover:text-white transition-colors flex items-center gap-2"
                    >
                        <span>📋</span> Duplicate Playbook
                    </button>
                    {contextMenu.pb.ownerId === currentUser?.uid && (
                        <button
                            onClick={() => { handleRename(contextMenu.pb.id, contextMenu.pb.name); setContextMenu(null); }}
                            className="w-full text-left px-4 py-2 hover:bg-blue-600 hover:text-white transition-colors flex items-center gap-2"
                        >
                            <span>✏️</span> Rename Playbook
                        </button>
                    )}
                    {contextMenu.pb.ownerId === currentUser?.uid && (
                        <>
                            <div className="h-px bg-gray-800 my-1"></div>
                            <button
                                onClick={() => { handleToggleDuplication(contextMenu.pb.id, contextMenu.pb.preventDuplication); setContextMenu(null); }}
                                className="w-full text-left px-4 py-2 hover:bg-gray-800 transition-colors flex items-center justify-between group"
                            >
                                <div className="flex items-center gap-2">
                                    <span>🔒</span> Prevent Duplication
                                </div>
                                <div className={`w-8 h-4 rounded-full p-0.5 transition-colors ${contextMenu.pb.preventDuplication ? 'bg-blue-600' : 'bg-gray-700'}`}>
                                    <div className={`w-3 h-3 bg-white rounded-full transition-transform ${contextMenu.pb.preventDuplication ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                </div>
                            </button>
                            <button
                                onClick={() => {
                                    const newNotes = window.prompt("Enter playbook notes:", contextMenu.pb.notes || "");
                                    if (newNotes !== null) updatePlaybook(contextMenu.pb.id, { notes: newNotes });
                                    setContextMenu(null);
                                }}
                                className="w-full text-left px-4 py-2 hover:bg-gray-800 transition-colors flex items-center gap-2"
                            >
                                <span>📝</span> Edit Notes
                            </button>
                            <button
                                onClick={() => { handleDelete(contextMenu.pb.id, contextMenu.pb); setContextMenu(null); }}
                                className="w-full text-left px-4 py-2 hover:bg-red-900 text-red-100 transition-colors flex items-center gap-2"
                            >
                                <span>🗑️</span> Delete Playbook
                            </button>
                        </>
                    )}
                </div>
            )}

            <footer className="p-8 text-center border-t border-gray-900 mt-auto">
                <p className="text-xs text-gray-600">QuickRoute Beta • {isMasterMode ? 'MASTER MODE' : `CLOUD SYNC: ${process.env.REACT_APP_FIREBASE_PROJECT_ID}`}</p>
            </footer>
        </div>
    );
};

export default HomeView;
